import React, {useCallback, useEffect, useState} from 'react';
import {useAddCommentLabel} from '../../../../hooks/comments/useCommentsListGrid';
import {ToastType} from '../../../../shared/constants';

import {AxiosError} from 'axios';
import {AddDocumentCommentResponse} from '../../../../api/types/document.type';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import {AddCommentDialog} from '../add-comment-dialog/AddCommentDialog';
import './AddCommentWrapper.scss';

export interface AddCommentWrapperProps {
  showModal?: boolean;
  securityGroupId?: number;
  onAddComment?: () => void;
  onClose?: (showModal: boolean) => void;
}

const AddCommentWrapper: React.FC<AddCommentWrapperProps> = ({
  showModal,
  securityGroupId = 0,
  onAddComment,
  onClose,
}: AddCommentWrapperProps) => {
  const [showToast, setShowToast] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [addCommentSucessMessage, setAddCommentSucessMessage] =
    useState<string>('');
  const [addCommentError, setAddCommentError] = useState<string>('');
  const [showAddCommentDialog, setShowAddCommentDialog] = useState<boolean>(
    showModal ?? false
  );

  const {mutate: addDocumentNewComment} = useAddCommentLabel();

  useEffect(() => {
    setShowAddCommentDialog(showModal ?? false);
  }, [showModal, onClose]);

  useEffect(() => {
    !showAddCommentDialog && onClose?.(false);
  }, [onClose, showAddCommentDialog]);

  const onSuccessAddComment = useCallback(
    (data: AddDocumentCommentResponse) => {
      onAddComment?.();
      setLoading(false);
      setAddCommentSucessMessage(data?.message ?? '');
      setShowAddCommentDialog(false);
      setShowToast(true);
    },
    [onAddComment]
  );

  const onErrorAddComment = useCallback((error: Error) => {
    const errorData: any = (error as AxiosError)?.response?.data;
    setAddCommentError(
      errorData?.detail?.split('\n')?.[0] ?? errorData?.title ?? 'api-error'
    );
    setLoading(false);
  }, []);

  const handleAddComment = useCallback(
    (commentLabel: string) => {
      setAddCommentSucessMessage('');
      setAddCommentError('');
      setLoading(true);
      addDocumentNewComment(
        {commentLabel, securityGroupId, isCommentEdited: false},
        {
          onSuccess: onSuccessAddComment,
          onError: onErrorAddComment,
        }
      );
    },
    [
      addDocumentNewComment,
      onErrorAddComment,
      onSuccessAddComment,
      securityGroupId,
    ]
  );

  const onCloseAddCommentDialog = useCallback(() => {
    setShowAddCommentDialog(false);
    setAddCommentError('');
  }, []);

  const onCloseToast = useCallback(() => {
    setShowToast(false);
    setAddCommentError('');
    setAddCommentSucessMessage('');
  }, []);

  return (
    <>
      <AddCommentDialog
        showModal={showAddCommentDialog}
        customError={addCommentError}
        requestLoading={loading}
        onSave={handleAddComment}
        onClose={onCloseAddCommentDialog}
      />
      {showToast && (
        <ArrToastNotification
          message={addCommentSucessMessage}
          status={[ToastType.Success]}
          onClose={onCloseToast}
        />
      )}
    </>
  );
};

export default AddCommentWrapper;
